import { Injectable } from '@angular/core';
import { Observable, map, BehaviorSubject } from 'rxjs';
import { IGetSqlDataParams, SqlResponse } from '@cdw-ae/filter-bar-interfaces';
import { ICdwSearchGridDataService } from '@cdw-ae/common-ui-components-search-grid';
import { ChromeAuditEvent } from './chrome-audit-event.interface';
import { DevicesDataService } from '../../../../../../common/services/device/devices-data.service';

/**
 * Data service for Chrome Audit Events
 *
 * Uses the getDeviceLogs API endpoint which fetches audit events from Google Admin Reports API.
 *
 * NOTE: This data service is fully functional and connected to the real API.
 * The Chrome Audit Events tab is currently hidden behind a "Coming soon..." overlay.
 * See chrome-audit-events.component.ts for instructions to enable the feature.
 */
@Injectable({
  providedIn: 'root',
})
export class ChromeAuditEventsDataService implements ICdwSearchGridDataService<ChromeAuditEvent> {
  private deviceId: string | null = null;
  private isEmptySubject = new BehaviorSubject<boolean>(false);
  public isEmpty$ = this.isEmptySubject.asObservable();

  constructor(private devicesDataService: DevicesDataService) {}

  /**
   * Set the device ID for fetching audit events
   */
  setDeviceId(deviceId: string | null): void {
    this.deviceId = deviceId;
  }

  /**
   * Fetches audit event data for the search grid
   *
   * @param params - Filter and pagination parameters
   * @returns Observable of paginated audit event data
   */
  fetchSqlData(params: IGetSqlDataParams): Observable<SqlResponse<ChromeAuditEvent>> {
    if (!this.deviceId) {
      this.isEmptySubject.next(true);
      return new Observable((observer) => {
        observer.next({ data: [], itemTotal: 0 });
        observer.complete();
      });
    }

    return this.devicesDataService.getDeviceLogs(this.deviceId).pipe(
      map((auditLogs: Record<string, unknown>[] | Record<string, unknown> | null) => {
        if (!auditLogs || (Array.isArray(auditLogs) && auditLogs.length === 0)) {
          this.isEmptySubject.next(true);
          return { data: [], itemTotal: 0 };
        }

        // Ensure we're working with an array
        const logsArray = Array.isArray(auditLogs) ? auditLogs : [auditLogs];

        // Add required 'id' field for CDW Search Grid
        const events: ChromeAuditEvent[] = logsArray.map((log, index) => ({
          ...log,
          id: `${log.eventType || 'unknown'}-${log.date || index}`,
        } as ChromeAuditEvent));

        // Handle client-side pagination
        const startRow = params.startRow || 0;
        const endRow = params.endRow || events.length;
        const pageData = events.slice(startRow, endRow);

        this.isEmptySubject.next(events.length === 0);

        return {
          data: pageData,
          itemTotal: events.length,
        };
      })
    );
  }
}
